import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ValidationError,
    useArealplan,
    useArealplanSlettegrunnlag,
    useDeleteArealplanMedTilhorende,
} from '@origo/planregister';
import { useToast } from '@origo/origo-components';
import PlanActions, { type PlanDeleteDialogState } from './PlanActions';
import { forgetRecentPlan } from '../utils/recentPlans';

interface PlanActionsContainerProps {
    napId: string;
    /** Kalles når planen er slettet, så viewet kan gå tilbake til søk. */
    onDeleted: () => void;
}

const PlanActionsContainer: React.FC<PlanActionsContainerProps> = ({ napId, onDeleted }) => {
    const { t } = useTranslation();
    const toast = useToast();
    const { data: plan } = useArealplan(napId);

    // --- slett ---
    // Slettegrunnlaget (blokkering + hva som slettes med planen) hentes ferskt
    // når dialogen åpnes, så blokkeringen ikke bygger på utdatert cache.
    const [deleteOpen, setDeleteOpen] = useState(false);
    const grunnlag = useArealplanSlettegrunnlag(plan ?? undefined, deleteOpen);
    const remove = useDeleteArealplanMedTilhorende();

    const confirmDelete = useCallback(async () => {
        if (!plan || !grunnlag.data || grunnlag.data.blokkering) return;
        const ok = await toast.execute(
            async () => {
                try {
                    await remove.mutateAsync(plan);
                } catch (err) {
                    // Blokkering oppdaget på nytt i use-casen har en presis melding.
                    if (err instanceof ValidationError) throw err;
                    throw new Error(t('planDetail.delete.error'));
                }
                return true;
            },
            { success: t('planDetail.delete.deleted') }
        );
        if (ok) {
            setDeleteOpen(false);
            forgetRecentPlan(plan.nasjonalArealplanId);
            onDeleted();
        }
    }, [plan, grunnlag.data, toast, remove, t, onDeleted]);

    if (!plan) return null;

    const deleteDialog: PlanDeleteDialogState = {
        open: deleteOpen,
        loading: grunnlag.isFetching || !grunnlag.data,
        blokkering: grunnlag.data?.blokkering ?? null,
        navn: plan.plannavn ?? '',
        behandlinger: grunnlag.data?.behandlinger.length ?? 0,
        dokumenter: grunnlag.data?.dokumenter.length ?? 0,
        isDeleting: remove.isPending,
        onCancel: () => setDeleteOpen(false),
        onConfirm: confirmDelete,
    };

    return <PlanActions onDeleteRequest={() => setDeleteOpen(true)} deleteDialog={deleteDialog} />;
};

export default PlanActionsContainer;
